import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../services/ApiService';
import { useToast } from '../contexts/ToastContext';
import PageHeader from './ui/PageHeader';
import Button from './ui/Button';
import ConfirmDialog from './ui/ConfirmDialog';
import AdminQueueState from './ui/AdminQueueState';
import PageLoader from './ui/PageLoader';

type BadgeDef = {
  id: number;
  name?: string;
  title?: string;
  status?: string;
  public_id?: string;
  updated_at?: string;
};

export const AdminBadgeModeration: React.FC = () => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [definitions, setDefinitions] = useState<BadgeDef[]>([]);
  const [audit, setAudit] = useState<Array<Record<string, unknown>>>([]);
  const [archiveId, setArchiveId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = (await apiService.getAdminBadgeModeration()) as {
        data?: { definitions?: BadgeDef[]; audit?: Array<Record<string, unknown>> };
        definitions?: BadgeDef[];
        audit?: Array<Record<string, unknown>>;
      };
      setDefinitions(res.data?.definitions || res.definitions || []);
      setAudit(res.data?.audit || res.audit || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load badge moderation');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const archive = async () => {
    if (!archiveId) return;
    try {
      setBusy(true);
      await apiService.adminBadgeModeration('archive_definition', { definition_id: archiveId });
      showToast('Badge definition archived', 'success');
      setArchiveId(null);
      await load();
    } catch (err: any) {
      showToast(err.message || 'Archive failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <PageLoader message="Loading badge moderation…" />;
  if (error) {
    return (
      <AdminQueueState
        variant="error"
        title="Badge moderation unavailable"
        description={error}
        onRetry={() => void load()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog
        isOpen={archiveId !== null}
        title="Archive badge definition?"
        description="Archived definitions stop issuing. Existing issued badges are not automatically revoked."
        confirmLabel="Archive"
        cancelLabel="Cancel"
        loading={busy}
        onCancel={() => setArchiveId(null)}
        onConfirm={() => void archive()}
      />

      <PageHeader
        eyebrow="Admin"
        title="Badge moderation"
        description="Review badge definitions and recent moderation audit entries."
        backLink={{ to: '/admin', label: 'Overview' }}
      />

      {definitions.length === 0 ? (
        <AdminQueueState
          variant="empty"
          title="No badge definitions"
          description="Definitions appear here when badges are configured."
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-white/[0.06]">
          <table className="min-w-full text-sm">
            <thead className="bg-white/[0.03] text-left text-gray-400">
              <tr>
                <th className="px-4 py-3 font-medium" scope="col">
                  Name
                </th>
                <th className="px-4 py-3 font-medium" scope="col">
                  Status
                </th>
                <th className="px-4 py-3 font-medium" scope="col">
                  Updated
                </th>
                <th className="px-4 py-3 font-medium" scope="col">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {definitions.map((d) => (
                <tr key={d.id} className="border-t border-white/[0.06]">
                  <td className="px-4 py-3 text-white">{d.name || d.title || `#${d.id}`}</td>
                  <td className="px-4 py-3 text-gray-300">{d.status || '—'}</td>
                  <td className="px-4 py-3 text-gray-400">{d.updated_at || '—'}</td>
                  <td className="px-4 py-3">
                    {d.status !== 'archived' ? (
                      <Button
                        type="button"
                        variant="secondary"
                        className="min-h-[40px]"
                        onClick={() => setArchiveId(d.id)}
                      >
                        Archive
                      </Button>
                    ) : (
                      <span className="text-gray-500">Archived</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <section aria-labelledby="badge-audit-heading">
        <h2 id="badge-audit-heading" className="text-lg font-semibold text-white mb-3">
          Recent audit
        </h2>
        {audit.length === 0 ? (
          <p className="text-sm text-gray-500">No audit rows yet.</p>
        ) : (
          <ul className="space-y-2 text-sm text-gray-300">
            {audit.slice(0, 25).map((row, i) => (
              <li key={i} className="rounded-lg border border-white/[0.06] px-3 py-2">
                <span className="text-white">{String(row.action || row.event || 'action')}</span>
                <span className="text-gray-500 ml-2">{String(row.created_at || '')}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default AdminBadgeModeration;
